import React, { useState, useEffect } from 'react';
import { database, ref, get, update } from '../../firebase';
import { Search, MessageSquare, X, Filter, RefreshCw, Send, ChevronDown, ChevronUp } from 'lucide-react';

const TicketReport = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [expandedTickets, setExpandedTickets] = useState({});

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const snapshot = await get(ref(database, 'UserTicket'));
      const allTickets = [];

      if (snapshot.exists()) {
        // Iterate through date folders
        snapshot.forEach(dateSnap => {
          const date = dateSnap.key;
          const dateData = dateSnap.val();
          
          // Iterate through users (using sponsorId)
          Object.keys(dateData).forEach(sponsorId => {
            const userTickets = dateData[sponsorId];
            
            // Iterate through ticket numbers
            Object.keys(userTickets).forEach(ticketKey => {
              const ticket = userTickets[ticketKey];
              
              // Ensure replies array exists and mark unread for admin
              const replies = ticket.replies || [];
              
              // Count unread replies for admin
              const unreadCount = replies.filter(r => 
                r.senderType === 'user' && !r.read
              ).length;
              
              allTickets.push({
                id: `${date}/${sponsorId}/${ticketKey}`,
                date: date,
                sponsorId: sponsorId,
                ticketKey: ticketKey,
                firebasePath: `UserTicket/${date}/${sponsorId}/${ticketKey}`,
                replies: replies,
                unreadCount: unreadCount,
                ...ticket
              });
            });
          });
        });
      }

      // Sort by date (newest first)
      allTickets.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setTickets(allTickets);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTicketExpand = (ticketId) => {
    setExpandedTickets(prev => ({
      ...prev,
      [ticketId]: !prev[ticketId]
    }));
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setReplyText('');
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    
    setSendingReply(true);
    try {
      const ticketRef = ref(database, selectedTicket.firebasePath);
      const snapshot = await get(ticketRef);
      
      if (snapshot.exists()) {
        const ticketData = snapshot.val();
        const currentReplies = ticketData.replies || [];
        
        // First, mark all existing user replies as read
        const updatedReplies = currentReplies.map(reply => {
          if (reply.senderType === 'user') {
            return { ...reply, read: true };
          }
          return reply;
        });
        
        // Create new admin reply
        const newReply = {
          id: `reply_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          text: replyText,
          sender: 'Admin',
          senderName: 'Support Team',
          senderType: 'admin',
          timestamp: new Date().toISOString(),
          read: true // Admin has read their own reply
        };
        
        // Add new reply
        updatedReplies.push(newReply);
        
        // Update ticket in database
        await update(ticketRef, {
          replies: updatedReplies,
          lastUpdated: new Date().toISOString(),
          status: ticketData.status === 'completed' ? 'in-progress' : ticketData.status || 'in-progress'
        });
        
        // Update local state
        const updatedTickets = tickets.map(t => {
          if (t.id === selectedTicket.id) {
            return {
              ...t,
              replies: updatedReplies,
              lastUpdated: new Date().toISOString(),
              unreadCount: 0
            };
          }
          return t;
        });
        
        setTickets(updatedTickets);
        setSelectedTicket({
          ...selectedTicket,
          replies: updatedReplies,
          lastUpdated: new Date().toISOString(),
          unreadCount: 0
        });
        
        setReplyText('');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply. Please try again.');
    } finally {
      setSendingReply(false);
    }
  };

  const handleMarkAsRead = async (ticket) => {
    try {
      const ticketRef = ref(database, ticket.firebasePath);
      const snapshot = await get(ticketRef);
      
      if (snapshot.exists()) {
        const ticketData = snapshot.val();
        const currentReplies = ticketData.replies || [];
        
        // Mark all user replies as read
        const updatedReplies = currentReplies.map(reply => {
          if (reply.senderType === 'user') {
            return { ...reply, read: true };
          }
          return reply;
        });
        
        await update(ticketRef, { replies: updatedReplies });
        
        // Update local state
        const updatedTickets = tickets.map(t => {
          if (t.id === ticket.id) {
            return { ...t, replies: updatedReplies, unreadCount: 0 };
          }
          return t;
        });
        
        setTickets(updatedTickets);
        
        if (selectedTicket?.id === ticket.id) {
          setSelectedTicket({ ...selectedTicket, replies: updatedReplies, unreadCount: 0 });
        }
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = searchTerm === '' ||
      ticket.sponsorId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.ticketId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.userName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      ticket.status?.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase() || 'open';
    const colors = {
      open: 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30',
      pending: 'bg-orange-500/20 text-orange-500 border border-orange-500/30',
      'in-progress': 'bg-blue-500/20 text-blue-500 border border-blue-500/30',
      completed: 'bg-green-500/20 text-green-500 border border-green-500/30',
      closed: 'bg-gray-500/20 text-gray-500 border border-gray-500/30'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[s] || colors.open}`}>
        {s.charAt(0).toUpperCase() + s.slice(1)}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const p = priority?.toLowerCase() || 'medium';
    const colors = {
      urgent: 'bg-red-500/20 text-red-500 border border-red-500/30',
      high: 'bg-orange-500/20 text-orange-500 border border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30',
      low: 'bg-blue-500/20 text-blue-500 border border-blue-500/30'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[p] || colors.medium}`}>
        {p.charAt(0).toUpperCase() + p.slice(1)}
      </span>
    );
  };

  const getProblemTypeIcon = (type) => {
    const icons = {
      technical: '🖥️',
      payment: '💰',
      withdrawal: '💸',
      account: '🔐',
      investment: '📈',
      referral: '👥',
      other: '❓'
    };
    return icons[type] || '📝';
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
        <p className="text-center mt-2 text-sm text-muted-foreground">Loading tickets...</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Ticket Reports</h1>
            <p className="text-sm text-muted-foreground">{tickets.length} total tickets</p>
          </div>
          <button
            onClick={fetchTickets}
            className="p-2 bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="h-5 w-5 text-primary" />
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="pending">Pending</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
            }}
            className="px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-lg text-sm transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Tickets List */}
      {filteredTickets.length === 0 ? (
        <div className="text-center py-12 border border-border rounded-lg bg-card">
          <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Tickets Found</h3>
          <p className="text-sm text-muted-foreground mb-6">No tickets match your search criteria</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTickets.map((ticket) => (
            <div 
              key={ticket.id} 
              className="bg-card border-2 border-border rounded-lg overflow-hidden hover:border-primary/50 transition-all duration-200"
            >
              {/* Ticket Summary - Clickable */}
              <div 
                className="p-4 cursor-pointer hover:bg-secondary/50 transition-colors"
                onClick={() => toggleTicketExpand(ticket.id)}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-xl">{getProblemTypeIcon(ticket.problemType)}</span>
                    <div className="flex-1">
                      <h3 className="font-medium text-foreground flex items-center gap-2">
                        {ticket.subject}
                        {ticket.unreadCount > 0 && (
                          <span className="bg-red-500/20 text-red-400 text-xs px-2 py-0.5 rounded-full border border-red-500/30">
                            {ticket.unreadCount} new
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        {ticket.userName} • {ticket.sponsorId}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(ticket.status)}
                    {expandedTickets[ticket.id] ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2 text-xs">
                  {getPriorityBadge(ticket.priority)}
                  <span className="bg-secondary/50 px-2 py-1 rounded">
                    {new Date(ticket.createdAt).toLocaleDateString()}
                  </span>
                  <span className="bg-secondary/50 px-2 py-1 rounded">
                    ID: {ticket.ticketId}
                  </span>
                  <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    {ticket.replies?.length || 0}
                  </span>
                </div>
              </div>

              {/* Expanded View - Show Conversation */}
              {expandedTickets[ticket.id] && (
                <div className="border-t border-border bg-secondary/30 p-4">
                  {/* Original Message */}
                  <div className="mb-4 bg-card border border-border rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-foreground">{ticket.userName}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(ticket.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{ticket.description}</p>
                  </div>

                  {/* All Replies */}
                  {ticket.replies && ticket.replies.length > 0 && (
                    <div className="space-y-3 mb-4">
                      {ticket.replies.map((reply, index) => (
                        <div 
                          key={reply.id || index} 
                          className={`flex ${reply.senderType === 'admin' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[85%] ${
                            reply.senderType === 'admin' 
                              ? 'bg-primary/20 border-primary/30' 
                              : 'bg-card border-border'
                          } border rounded-lg p-3`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-foreground">
                                {reply.senderType === 'admin' ? 'Support Team' : ticket.userName}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(reply.timestamp).toLocaleString()}
                              </span>
                              {reply.senderType === 'user' && !reply.read && (
                                <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                                  New
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-foreground whitespace-pre-wrap">{reply.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewTicket(ticket)}
                      className="flex-1 px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <Send className="h-4 w-4" />
                      Reply
                    </button>
                    {ticket.unreadCount > 0 && (
                      <button
                        onClick={() => handleMarkAsRead(ticket)}
                        className="px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-lg text-sm font-medium transition-colors"
                      >
                        Mark Read
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      {filteredTickets.length > 0 && (
        <div className="mt-6 pt-6 border-t-2 border-border">
          <div className="text-sm text-muted-foreground">
            Showing {filteredTickets.length} of {tickets.length} tickets
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/80 transition-opacity"
            onClick={() => {
              setSelectedTicket(null);
              setReplyText('');
            }}
          />
          
          {/* Modal */}
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="relative bg-card border-2 border-border rounded-t-xl sm:rounded-xl w-full max-w-lg overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b-2 border-border flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-bold text-foreground">Reply to Ticket</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedTicket.ticketId} • {selectedTicket.userName}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedTicket(null);
                    setReplyText('');
                  }}
                  className="p-2 hover:bg-secondary rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-muted-foreground" />
                </button>
              </div>
              
              {/* Content */}
              <div className="p-4 max-h-[60vh] overflow-y-auto">
                {/* Recent Messages Preview */}
                <div className="mb-4 bg-secondary/30 rounded-lg p-3 max-h-32 overflow-y-auto">
                  <p className="text-xs text-muted-foreground mb-2">Recent messages:</p>
                  {selectedTicket.replies?.slice(-3).map((reply, idx) => (
                    <div key={idx} className="text-xs mb-1 pb-1 border-b border-border/30 last:border-0">
                      <span className={reply.senderType === 'admin' ? 'text-primary' : 'text-foreground'}>
                        {reply.senderType === 'admin' ? 'You: ' : `${selectedTicket.userName}: `}
                      </span>
                      <span className="text-muted-foreground">{reply.text.substring(0, 50)}...</span>
                    </div>
                  ))}
                </div>
                
                {/* Reply Input */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Your Reply:
                  </label>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply here..."
                    rows={5}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                    autoFocus
                  />
                </div>
              </div>
              
              {/* Footer */}
              <div className="p-4 border-t-2 border-border flex gap-2">
                <button
                  onClick={() => {
                    setSelectedTicket(null);
                    setReplyText('');
                  }}
                  className="flex-1 py-3 border border-border rounded-lg text-sm font-medium hover:bg-secondary/50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendReply}
                  disabled={!replyText.trim() || sendingReply}
                  className="flex-1 py-3 bg-primary hover:bg-primary/80 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sendingReply ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send Reply
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketReport;